(function () {
  /**
   * @ngdoc component
   * @name abxPinDetails
   *
   * @param {Function} onUpdate - Called on pin updates.
   * @param {Object} pin - Pin responsible for
   * @param {Boolean} [saveOnBlur] - If inputs should save on blur. For a pin
   *    details, this might be desirable. But, for something like a pin
   *    creation, in which the inputs are simply part of their parent form,
   *    this would be undesirable. Defaults to false.
   * @param {Object} [disabledFieldTypes] - A hash of pinfield types to booleans
   *    if a type is omitted, it will not be disabled. Set the value at a given
   *    type to true to disable that type. This overrides the is_editable=true
   *    pinField property.
   * @param {Object} permissions - Optional user permissions object to determine if input
   *    components should be disabled. Null if flexible_client_permissions is disabled
   *
   * @description
   * Contains the editable fields for the given pin. This includes fields
   * that are directly on the pin, or pin values embedded in its `values`
   * field.
   *
   * Incoming pins will have their values evaulated (if available), and
   * pin value data will be used to select the floor filter for Room type
   * inputs.
   */
  angular
    .module("akitabox.ui.components.pinDetails")
    .component("abxPinDetails", {
      bindings: {
        onUpdate: "&abxOnUpdate",
        pin: "<abxPin",
        saveOnBlur: "<?abxSaveOnBlur",
        disabledFieldTypes: "<?abxDisabledFieldTypes",
        permissions: "<abxPermissions",
        onAttachmentUploadStart: "<?abxOnAttachmentUploadStart",
        onAttachmentUploadsFinished: "<?abxOnAttachmentUploadsFinished",
      },
      controller: AbxPinDetailsController,
      controllerAs: "vm",
      templateUrl:
        "app/core/ui/components/pin-details/pin-details.component.html",
    });

  function AbxPinDetailsController(
    // Angular
    $scope,
    $window,
    // Services
    FeatureFlagService,
    OrganizationService,
    ToastService,
    Utils,
    // Dialogs
    DownloadDocumentDialog,
    OcrDialog,
    FilePreviewDialog,
    //Model
    models
  ) {
    var self = this;

    var organization = OrganizationService.getCurrent();

    // Attributes
    self.disabledFieldTypes = self.disabledFieldTypes || {};
    self.isAsset = false;
    self.isRoom = false;
    self.isQRCodeEditable = isQRCodeEditable();
    self.showLifeCycle = organization.show_rpi_fci;
    self.webRoomsAssetsFF = FeatureFlagService.isEnabled(
      "web_rooms_assets_bffs"
    );
    self.preProcessFlag =
      FeatureFlagService.isEnabled("preprocess-ocr") || false;

    // Stores the current pinType's floor pinField id
    self.floorPinFieldId = null;

    // Stores the current pin's floor id
    self.pinFloorId = null;

    // Functions
    self.isDisabled = isDisabled;
    self.isEditable = isEditable;
    self.updatePinValue = updatePinValue;
    self.updateField = updateField;
    self.openAttachment = openAttachment;

    // =================
    // Lifecycle
    // =================

    self.$onChanges = function (changes) {
      if (
        changes.disabledFieldTypes &&
        !angular.isObject(self.disabledFieldTypes)
      ) {
        self.disabledFieldTypes = {};
      }
      if (changes.pin) {
        if (self.pin) {
          // Copy pin type to force inputs to recompile for a new pin of the same
          // pin type; otherwise, the state of the inputs for the old pin would
          // be reflected for the new pin
          if (!Utils.isSameModel(self.pin, changes.pin.previousValue)) {
            self.pinType = angular.copy(self.pin.pinType);
          }

          if (haveNewPinType()) {
            // Update pin model for new pin type
            determinePinModel();
            determineLevelPinField();
          }
          if (self.floorPinFieldId && pinHasLevelValue()) {
            determinePinLevel();
          } else {
            self.pinFloorId = null;
          }
        } else {
          self.floorPinFieldId = null;
          self.pinFloorId = null;
        }
      }

      /**
       * Determine if the new pin has a different pin type than before.
       */
      function haveNewPinType() {
        return (
          !changes.pin.previousValue ||
          changes.pin.isFirstChange() ||
          self.pin.pinType._id !== changes.pin.previousValue.pinType._id
        );
      }
    };

    // =================
    // Public Functions
    // =================

    function isDisabled(pinField) {
      var dataTypeDisabled = self.disabledFieldTypes[pinField.data_type];
      if (self.permissions && pinField.data_type === "room") {
        return dataTypeDisabled || !self.permissions.canChangeLocation;
      }
      // (WEBAPP-9092) remove
      return dataTypeDisabled;
    }

    function isEditable(pinField) {
      if (self.permissions) {
        var canEditPin = self.permissions.canEditPin;
        // Allow room to be editable even if the user can't edit the pin (asset)
        // If the user can edit the pin but not the pin's room it will be
        // disabled via `isDisabled`, that way it looks like the other inputs
        if (pinField.data_type === "room" && !canEditPin) {
          return self.permissions.canChangeLocation;
        }
        return canEditPin;
      }
      // (WEBAPP-9092) remove
      return true;
    }

    /**
     * Update a standard field on a pin.
     *
     * @param {Object} event - Propagated event
     * @param {*} event.newValue - New value for the field
     * @param {String} field - Key of field being updated
     */
    function updateField(event, field) {
      if (!self.saveOnBlur) return;

      event.field = field;

      return self.onUpdate({
        $event: event,
      });
    }

    /**
     * Saves the pin with an updated pin value.
     *
     * @param {Object} event - Propagated event
     * @param {*} event.newValue - Value to save pin value with
     * @param {Object} pinField - Pin field associated with pin value
     */
    function updatePinValue(event, pinField) {
      if (!self.saveOnBlur) return;

      event.field = "values";
      event.pinField = pinField;

      return self.onUpdate({
        $event: event,
      });
    }

    // =================
    // Private Functions
    // =================

    function isQRCodeEditable() {
      if (self.permissions) {
        return (
          self.permissions.canAssociateQRCodes &&
          self.permissions.canDisassociateQRCodes
        );
      }
      // (WEBAPP-9092) remove
      return true;
    }

    /**
     * Check if self.pin has the current level field as a value
     *
     * @return {Boolean} true iff the current pin has a pinValue with id matching
     *    self.floorPinFieldId
     */
    function pinHasLevelValue() {
      return (
        self.pin && self.pin.values && self.pin.values[self.floorPinFieldId]
      );
    }

    /**
     * Determine the ID for the current pin's level PinField, updates
     * self.floorPinFieldId.
     */
    function determineLevelPinField() {
      if (self.webRoomsAssetsFF) {
        const pinValues = self.pin.values;
        for (const value in pinValues) {
          if (Object.hasOwnProperty.call(pinValues, value)) {
            if (pinValues[value].is_level_pin_value) {
              self.floorPinFieldId = value;
              return;
            }
          }
        }
      } else {
        var pinFields = self.pin.pinType.fields;
        for (let i = 0; i < pinFields.length; i++) {
          if (pinFields[i].is_level_field) {
            self.floorPinFieldId = pinFields[i]._id;
            return;
          }
        }
      }
      self.floorPinFieldId = null;
    }

    /**
     * Sets self.pinFloorId to the current pin's level's id.
     *
     * relies on self.floorPinFieldId
     */
    function determinePinLevel() {
      self.pinFloorId = self.pin.values[self.floorPinFieldId].value;
    }
    /**
     * Determine which model of pin we have, and update any data that needs
     * to change based on it.
     */
    function determinePinModel() {
      $scope.$evalAsync(function () {
        self.isAsset = self.pinType.protected_type === "Asset";
        self.isRoom = self.pinType.protected_type === "Room";
        self.entityType = self.isAsset ? "asset" : "room";
      });
    }

    /**
     * This function overwrites the function used
     * to open the attachment on <abx-attachment-thumbnail> component
     */
    function openAttachment($event) {
      if (!$event || !$event.document || !self.pin) {
        ToastService.showError("Document selected is not available");
      } else {
        const { document, documentOrder } = $event;
        const supportedExtension =
          models.DOCUMENT.FILE_PREVIEW_SUPPORTED_TYPES.includes(
            document.extension.toLowerCase()
          );

        if (supportedExtension) {
          if (self.preProcessFlag) {
            FilePreviewDialog.show({
              pin: self.pin,
              documentId: document._id,
              supportMultipleFiles: true,
              documentOrder,
            });
          } else {
            OcrDialog.show({ document: document });
          }
        } else {
          var locals = {
            docs: [document],
          };
          DownloadDocumentDialog.show({
            locals: locals,
          }).catch(ToastService.showError);
        }
      }
    }
  }
})();
